import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, FileText, Lock, CheckCircle } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

const protections = [
  {
    icon: Lock,
    title: "Centralized IP Ownership",
    description: "All systems, frameworks, and methodologies are owned by OnPoint Authority Systems, Inc.",
  },
  {
    icon: FileText,
    title: "Licensed Operator Usage",
    description: "Operators deploy systems under formal licensing agreements with defined terms.",
  },
  {
    icon: Shield,
    title: "Enforcement & Revocation Rights",
    description: "Authority to enforce compliance and revoke access for violations.",
  },
  {
    icon: CheckCircle,
    title: "Brand & System Integrity",
    description: "Safeguards to maintain quality and consistency across all deployments.",
  },
];

const LegalPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="legal-page">
      <SEOHead 
        title="Governance, IP & Brand Protection"
        description="Legal framework, intellectual property ownership, and brand protection for OnPoint Authority Systems."
        url="https://onpointauthority.com/legal"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-6 md:px-12" data-testid="legal-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Legal Framework
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Governance, IP &<br />
              <span className="text-primary">Brand Protection</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-2xl">
              All systems, certifications, and deployments operate under formal IP ownership, 
              licensing agreements, and governance controls.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Protections */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="protections-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Framework
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground">
              Protections<br />
              <span className="text-primary">Include</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {protections.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card/5 border border-white/5 p-8"
                data-testid={`protection-${index}`}
              >
                <item.icon className="h-8 w-8 text-primary mb-4" strokeWidth={1.5} />
                <h3 className="font-serif text-xl text-foreground mb-3">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Corporate Entity */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="entity-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Corporate Entity
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground mb-8">
              OnPoint Authority<br />
              <span className="text-primary">Systems, Inc.</span>
            </h2>

            <div className="border border-white/10 bg-card/5 p-8 mb-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                <div>
                  <p className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2">
                    Jurisdiction
                  </p>
                  <p className="text-foreground">Delaware Corporation</p>
                </div>
                <div>
                  <p className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2">
                    IP Status
                  </p>
                  <p className="text-foreground">Proprietary, Documented</p>
                </div>
                <div>
                  <p className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2">
                    Licensing
                  </p>
                  <p className="text-foreground">Operator Framework Active</p>
                </div>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mb-8">
              All rights reserved. Unauthorized use, reproduction, or distribution prohibited.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/privacy">
                <Button
                  variant="outline"
                  className="border border-white/10 bg-transparent hover:border-primary/50 hover:text-primary uppercase tracking-[0.15em] text-xs font-bold px-6 py-4 rounded-none"
                >
                  Privacy Policy
                </Button>
              </Link>
              <Link to="/terms">
                <Button
                  variant="outline"
                  className="border border-white/10 bg-transparent hover:border-primary/50 hover:text-primary uppercase tracking-[0.15em] text-xs font-bold px-6 py-4 rounded-none"
                >
                  Terms of Service
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LegalPage;
